import React, { useState, useEffect, useRef } from 'react';
import IntegrationsMarquee from '../components/IntegrationsMarquee';
import NetworkThrottler from '../components/NetworkThrottler';
import { GoogleLogin } from '@react-oauth/google';
import { apiClient } from '../utils/api';
import logo from '../Logo/logo.png';
import LandingNavbar from '../components/layout/LandingNavbar';
import {
  Download,
  Lock,
  ShieldCheck,
  Smartphone,
  Users,
  Zap,
  BarChart3,
  ChevronRight,
  Mail,
  Frown,
  Flame,
  DollarSign,
  HelpCircle,
  Video,
  MapPin,
  Bot,
  Share2,
  UserCheck,
  Wallet,
  MessageCircle,
  Rocket,
  CheckCircle2,
  CreditCard,
  Target,
  Bug,
  List,
  Github,
  Instagram,
  Linkedin,
  Plus,
  Minus
} from 'lucide-react';
import murphyGraphic from '../Logo/image.png';
import { AuroraText } from '../components/common/AuroraText';
import { TextAnimate } from '../components/common/TextAnimate';
import { AnimatedShinyText } from '../components/common/AnimatedShinyText';
import { ShineBorder } from '../components/common/ShineBorder';
import { InteractiveButton } from '../components/common/InteractiveButton';
import './HomePage.css';
import dashboardImg from '../assets/dashboard.png';
import recordingImg from '../assets/recordingplusspecs.png';
import { BuildersSection } from '../components/BuildersSection';
import HeroSection from '../components/HeroSection';
import SampleReportCard from '../components/SampleReportCard';

/* ── tiny hook: fade-up on scroll ── */
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.unobserve(el); } },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  console.log('Forcing Webpack rebuild...');
  return [ref, visible];
}

// -- HERO DASHBOARD PREVIEW COMPONENT --
// -- HERO DASHBOARD PREVIEW COMPONENT REMOVED --

/* ── FAQ Data & Component ── */
const FAQS = [
  {
    q: "How is this different from BrowserStack or Firebase Test Lab?",
    a: "Automated device farms can't tell you if an OTP SMS didn't arrive, if a UI element is hidden by a weird manufacturer notch, or if the checkout flow is confusing. We use real humans on actual mobile connections to give you qualitative context that automation completely misses."
  },
  {
    q: "What kind of devices do you test on?",
    a: "We target the harsh reality of the Indian market. Expect your app to be tested on 3-year-old Redmis, budget Infinix phones, and devices with aggressive battery savers, custom OEM skins, and almost zero free storage."
  },
  {
    q: "Are these real people or just bot farms?",
    a: "100% verified real humans. We recruit and verify testers from Tier 2 and Tier 3 cities across India. They use their personal daily-driver smartphones, and we pay them fairly per session completed."
  },
  {
    q: "Can I test pre-production or beta builds?",
    a: "Absolutely. You can provide APKs, TestFlight invites, or unlisted Google Play Store links. Our testers know how to side-load apps securely."
  },
  {
    q: "How long does it take to get a report?",
    a: "Most tests are picked up by our crowd within hours. You will receive the fully analyzed, AI-compiled bug report with screen recording evidence in less than 48 hours."
  },
  {
    q: "Is my app data secure?",
    a: "Yes. Our testers are strictly instructed to use dummy data in isolated environments. We never ask for backend database access or your source code."
  },
  {
    q: "When and how do I pay?",
    a: "You add a test and pay through UPI. No credit card required if you don't want to commit. After you place a test project on our site, you will be emailed a bill with a QR code."
  }
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`hp-faq-item ${open ? 'hp-faq-item--open' : ''}`}>
      <button className="hp-faq-q" onClick={() => setOpen(!open)}>
        <span>{q}</span>
        <span className="hp-faq-icon">{open ? <Minus size={18} /> : <Plus size={18} />}</span>
      </button>
      <div className="hp-faq-a">
        <div className="hp-faq-a-inner">{a}</div>
      </div>
    </div>
  );
}

export default function HomePage({ company, onLogin, onNavigate }) {
  const [latestApk, setLatestApk] = useState(null);
  const [dlRef, dlVis] = useReveal(0.15);

  useEffect(() => {
    // Fetch latest APK link
    apiClient.getLatestAppVersion()
      .then(data => { if (data?.latest?.apk_url) setLatestApk(data.latest); })
      .catch(() => { });
  }, []);

  const [heroRef, heroVis] = useReveal(0.1);
  const [numRef, numVis] = useReveal(0.2);
  const [painRef, painVis] = useReveal(0.15);
  const [stepsRef, stepsVis] = useReveal(0.1);
  const [featRef, featVis] = useReveal(0.1);
  const [murphyRef, murphyVis] = useReveal(0.15);
  const [ctaRef, ctaVis] = useReveal(0.2);

  return (
    <div className="hp">

      {/* ── Navbar ── */}
      <LandingNavbar company={company} onNavigate={onNavigate} view="home" />

      {/* ── Hero ── */}
      <HeroSection onNavigate={onNavigate} />

      {/* ── Integrations Marquee ── */}
      <IntegrationsMarquee />

      {/* ── Network Throttler Demo ── */}
      <NetworkThrottler />

      {/* ── Murphy's Law Section ── */}
      <section className="hp-murphy-section">
        <div ref={murphyRef} className={`hp-murphy-inner reveal ${murphyVis ? 'reveal--on' : ''}`}>
          <div className="hp-murphy-content">
            <AnimatedShinyText className="hp-section-badge">The Golden Rule of Apps</AnimatedShinyText>
            <TextAnimate as="h2" animation="blurInUp" by="word" className="hp-murphy-h2">
              {"Murphy's Law: \"Anything that can go wrong, will go wrong.\""}
            </TextAnimate>
            <TextAnimate animation="blurInUp" by="word" className="hp-murphy-p">
              Problems show only after the software runs on a cheap handset that a person with limited cash uses in a place where the signal drops. The controlled room never matches that truth. BharatQA repeats that truth every day.
            </TextAnimate>
          </div>
          {/* float-ready added after 1.2s so the intro transition completes first */}
          <div className={`hp-murphy-graphic-wrap${murphyVis ? ' float-ready' : ''}`}
               style={{ transitionDelay: murphyVis ? '0.25s' : '0s' }}>
            <img src={murphyGraphic} alt="Murphy's Law Graphic" className="hp-murphy-graphic" />
            <div className="hp-murphy-glow" />
          </div>
        </div>
      </section>

      {/* ── Numbers strip ── */}
      <section className="hp-numbers">
        <div ref={numRef} className={`hp-numbers-inner reveal ${numVis ? 'reveal--on' : ''}`}>
          {[
            { display: '50+', label: 'Real device types' },
            { display: '<2 days', label: 'Time to first report' },
            { display: '100+', label: 'Testers paid per session' },
            { display: '24/7', label: 'Tester availability' },
          ].map((d, i) => (
            <div key={i} className="hp-num-block">
              <strong>{d.display}</strong>
              <span>{d.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Real testers section ── */}
      <section className="hp-testers-section">
        <div className="hp-testers-inner">
          <div className="hp-testers-left">
            <AnimatedShinyText className="hp-section-badge">Human-powered testing</AnimatedShinyText>
            <TextAnimate as="h2" animation="blurInUp" by="word" className="hp-section-title hp-section-title--left">
              Real people use real phones and give real feedback.
            </TextAnimate>
            <p className="hp-testers-desc">
              We connect your app with actual users across India. No bots, no simulators - just humans using physical Android devices to give honest, usable input.
            </p>
            <div className="hp-testers-perks">
              <div className="hp-tester-perk">
                <span className="hp-tester-perk-icon"><UserCheck size={20} /></span>
                <div>
                  <strong>Verified humans, not bots</strong>
                  <span>Each tester gets verified before they take a job No scripts. No automated behavior. They're real, not software-driven.</span>
                </div>
              </div>
              <div className="hp-tester-perk">
                <span className="hp-tester-perk-icon"><Wallet size={20} /></span>
                <div>
                  <strong>We pay testers fairly</strong>
                  <span>Testers get paid per session completed. We're open about it, that's how we keep things fair and honest.</span>
                </div>
              </div>
              <div className="hp-tester-perk">
                <span className="hp-tester-perk-icon"><Smartphone size={20} /></span>
                <div>
                  <strong>Their own devices</strong>
                  <span>They use the phones they own - Redmis, Samsungs, older Motorolas Not lab models. The real Indian market.</span>
                </div>
              </div>
              <div className="hp-tester-perk">
                <span className="hp-tester-perk-icon"><MessageCircle size={20} /></span>
                <div>
                  <strong>Qualitative feedback too</strong>
                  <span>We get more than crashes. Testers record what confuses them, what makes them stop, what brings joy. You hear how real users feel about your app.</span>
                </div>
              </div>
            </div>
          </div>
          <div className="hp-testers-right">
            <div className="hp-tester-card-stack">
              <div className="hp-tester-card">
                <div className="hp-tester-avatar">RK</div>
                <div className="hp-tester-info">
                  <strong>Rajesh Kannan</strong>
                  <span>Bengaluru · Redmi Note 11 · Android 12</span>
                </div>
                <div className="hp-tester-earning">₹30 earned</div>
              </div>
              <div className="hp-tester-card">
                <div className="hp-tester-avatar hp-tester-avatar--2">MI</div>
                <div className="hp-tester-info">
                  <strong>Meera Iyer</strong>
                  <span>Pune · Samsung M31 · Android 11</span>
                </div>
                <div className="hp-tester-earning">₹20 earned</div>
              </div>
              <div className="hp-tester-card">
                <div className="hp-tester-avatar hp-tester-avatar--3">SG</div>
                <div className="hp-tester-info">
                  <strong>Siddarth Gupta</strong>
                  <span>Jaipur · Moto G62 · Android 12</span>
                </div>
                <div className="hp-tester-earning">₹35 earned</div>
              </div>
              <div className="hp-tester-live-badge">
                <span className="hp-dot on" style={{ width: 8, height: 8 }} />
                3 testers live right now
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mobile-only tester CTA ── */}
      <div className="hp-mobile-dl-banner">
        <p><Smartphone size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} /> <strong>Earn money testing apps.</strong> Download the BharatQA Tester App.</p>
        {latestApk ? (
          <a href={latestApk.apk_url} className="hp-btn" style={{ textDecoration: 'none' }}>
            ⬇️ Download APK
          </a>
        ) : (
          <span className="hp-dl-card-loading">Loading download link…</span>
        )}
      </div>

      {/* ── Become a Tester / Download Section ── */}
      <section className="hp-dl-section" id="testers">
        <div ref={dlRef} className={`hp-dl-grid reveal ${dlVis ? 'reveal--on' : ''}`}>
          <div className="hp-dl-left">
            <AnimatedShinyText className="hp-section-badge"><Smartphone size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> For Testers</AnimatedShinyText>
            <TextAnimate as="h2" animation="blurInUp" by="word" className="hp-section-title hp-section-title--left">
              Make money. Try out real apps. Through your phone.
            </TextAnimate>
            <p>
              Interested in becoming a tester at BharatQA? Just download the app, sign up using your Google account, and start earning ₹20 to ₹35 for each test session done - all from your Android phone.
            </p>
            <ul className="hp-dl-perks">
              {[
                [<Zap key="ic1" size={18} />, 'No fees for joining, no funds needed'],
                [<Wallet key="ic2" size={18} />, 'Testers are paid once a week'],
                [<Smartphone key="ic3" size={18} />, 'Works on any Android phone (Android 8+)'],
                [<UserCheck key="ic4" size={18} />, 'Get matched to tests based on your device'],
              ].map(([icon, text], i) => (
                <li key={i}>
                  <span className="hp-dl-perk-icon">{icon}</span> {text}
                </li>
              ))}
            </ul>
          </div>
          <div className="hp-dl-right">
            <div className="hp-dl-card">
              <div className="hp-dl-card-icon"><Download size={32} /></div>
              <h3>Download BharatQA</h3>
              <p className="hp-dl-card-sub">Official tester app for Android</p>

              <div className="hp-dl-card-main">
                {latestApk ? (
                  <>
                    <div className="hp-dl-card-meta">
                      <span className="hp-dl-version-tag">STABLE</span>
                      <span className="hp-dl-version-num">Build v{latestApk.version_name}</span>
                    </div>
                    <a
                      href={latestApk.apk_url}
                      className="hp-btn hp-btn--download"
                      style={{ textDecoration: 'none' }}
                    >
                      Download APK
                    </a>
                  </>
                ) : (
                  <div className="hp-dl-card-loading">
                    <div className="spinner-sm" /> Connecting to server...
                  </div>
                )}
              </div>
            </div>
            <div className="hp-dl-contact">
              <span>fundup3@gmail.com</span>
              <span>·</span>
              <span>+91 6361434273</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pain points ── */}
      <section className="hp-section--pain">
        <div ref={painRef} className={`hp-pain-inner reveal ${painVis ? 'reveal--on' : ''}`}>
          <AnimatedShinyText className="hp-section-badge">The Reality</AnimatedShinyText>
          <TextAnimate as="h2" animation="blurInUp" by="word" className="hp-section-title">
            Sound familiar?
          </TextAnimate>
          <TextAnimate animation="blurInUp" by="word" className="hp-section-sub">
            Every Android dev has shipped something that worked perfectly on their machine.
          </TextAnimate>
          <div className="hp-evidence-grid">

            {/* EVIDENCE 1: Mock Play Store Review */}
            <div className="hp-evidence-card" style={{ position: 'relative' }}>
              <ShineBorder shineColor="#FF6B2B" duration={12} opacity={0.5} />
              <div style={{ fontSize: '11px', color: 'var(--hp-fg3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '16px' }}>
                Google Play Store · Review
              </div>
              <h3 style={{ marginBottom: '20px', fontSize: '16px', color: 'var(--hp-fg)' }}>
                1-star reviews before you even knew there was a bug
              </h3>
              <div className="hp-mock-review">
                <div className="hp-mock-stars">★☆☆☆☆</div>
                <div className="hp-mock-review-text" style={{ fontStyle: 'normal' }}>
                  "App crashes every time I try to checkout. Uninstalling."
                </div>
                <div className="hp-mock-review-meta">
                  <span>Ravi K. · Samsung Galaxy M13</span>
                  <span>2 weeks ago</span>
                </div>
              </div>
              <div style={{ marginTop: 'auto', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>Developer hasn't responded</div>
              </div>
            </div>

            {/* EVIDENCE 2: Mock Crash Log */}
            <div className="hp-evidence-card" style={{ position: 'relative' }}>
              <ShineBorder shineColor="#FF6B2B" duration={12} opacity={0.5} />
              <h3 style={{ marginBottom: '20px', fontSize: '16px', color: 'var(--hp-fg)' }}>
                Crashlytics tells you what crashed. Not why.
              </h3>
              <div className="hp-mock-log-container">
                <div className="hp-mock-log" style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <span className="hp-mock-log-error">Fatal Exception: java.lang.NullPointerException</span><br />
                  <span>  at com.bharatqa.app.CheckoutActivity.onCreate(CheckoutActivity.java:42)</span><br />
                  <span>  at android.app.Activity.performCreate(Activity.java:8000)</span><br />
                  <span>  at android.app.Instrumentation.callActivityOnCreate(Instrumentation.java:1309)</span><br />
                  <span>  at android.app.ActivityThread.performLaunchActivity(ActivityThread.java:3422)</span>
                </div>
                <div className="hp-mock-log-overlay" style={{ marginTop: 'auto' }}>
                  You know it crashed. You don't know what the user was doing.
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="hp-section" id="how-it-works">
        <div ref={stepsRef} className={`reveal ${stepsVis ? 'reveal--on' : ''}`}>
          <AnimatedShinyText className="hp-section-badge">The Process</AnimatedShinyText>
          <TextAnimate as="h2" animation="blurInUp" by="word" className="hp-section-title">
            How it works
          </TextAnimate>
          <TextAnimate animation="blurInUp" by="word" className="hp-section-sub">
            Three steps from APK to actionable bug reports.
          </TextAnimate>
          <div className="hp-steps">
            {[
              {
                n: 'Step 01',
                title: 'Upload your APK',
                text: 'Sign in with Google, drop your APK, and write simple test instructions — like "sign up, add an item to cart, checkout."',
              },
              {
                n: 'Step 02',
                title: 'Real people test on real phones',
                text: 'Testers across Indian cities install your app on their own devices — budget phones, mid-range, flagships — and record their entire session.',
              },
              {
                n: 'Step 03',
                title: 'AI turns recordings into bug reports',
                text: 'AI watches every recording, detects crashes, UI glitches, and slow screens, and generates prioritised bug reports you can act on immediately.',
              },
            ].map((s, i) => (
              <div key={i} className="hp-step" style={{ animationDelay: `${i * 120}ms` }}>
                <div className="hp-step-n">{s.n}</div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Technical Deep Dive ── */}
      <section className="hp-section--technical" id="technical-details">
        <div className="hp-technical-inner">
          <div className="hp-technical-header">
            <AnimatedShinyText className="hp-section-badge">No buzzwords. Just proof.</AnimatedShinyText>
            <h2 className="hp-section-title hp-section-title--left">
              How BharatQA Actually Works
            </h2>
          </div>

          <div className="hp-technical-stack" style={{
            marginTop: '24px',
            padding: '32px 0',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            borderBottom: '1px solid rgba(255,255,255,0.06)'
          }}>
            {/* Block 1: Real Device Coverage */}
            <div className="hp-tech-block" style={{ marginBottom: '36px', textAlign: 'center' }}>
              <div style={{ color: '#f97316', fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Real device coverage</div>
              <h3 style={{ color: '#fff', fontSize: '24px', fontWeight: 700, maxWidth: '560px', margin: '12px auto 0', lineHeight: 1.2 }}>Physical Android devices across different price segments and OEMs.</h3>
              <p style={{ color: '#9ca3af', fontSize: '15px', lineHeight: 1.7, maxWidth: '480px', margin: '16px auto 0' }}>
                Tests are performed on a growing pool of physical Android devices across different price segments and OEMs, covering Android 10 to 14. This includes everything from low-end 3GB RAM devices to mid-range flagships with varying screen sizes and DPIs.
              </p>

              <div style={{
                marginTop: '32px',
                maxWidth: '760px',
                margin: '32px auto 0',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px',
                padding: '24px',
                textAlign: 'left',
                overflowX: 'auto'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  paddingBottom: '12px',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  marginBottom: '16px'
                }}>
                  {['Device', 'OS', 'Network', 'Tier'].map(h => (
                    <span key={h} style={{ color: '#6b7280', fontSize: '11px', fontWeight: 500, textTransform: 'uppercase' }}>{h}</span>
                  ))}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {[
                    { name: 'Samsung SM-G991B', os: 'Android 13', net: 'Airtel 5G', tier: 'FLAGSHIP' },
                    { name: 'Redmi Note 12', os: 'Android 12', net: 'Jio 4G', tier: 'LOW-END' },
                    { name: 'Pixel 6a', os: 'Android 14', net: 'WiFi 6', tier: 'FLAGSHIP' },
                    { name: 'Vivo V2151', os: 'Android 12', net: 'Airtel 4G', tier: 'MID-RANGE' },
                    { name: 'Samsung SM-A336E', os: 'Android 13', net: 'Jio 5G', tier: 'MID-RANGE' },
                    { name: 'Motorola XT2213', os: 'Android 11', net: 'Vi 4G', tier: 'LOW-END' },
                    { name: 'OnePlus CPH2447', os: 'Android 13', net: 'WiFi', tier: 'FLAGSHIP' },
                    { name: 'Realme RMX3370', os: 'Android 12', net: 'Jio 4G', tier: 'MID-RANGE' }
                  ].map((d, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', alignItems: 'center' }}>
                      <span style={{ color: '#fff', fontSize: '13px', fontWeight: 500 }}>{d.name}</span>
                      <span style={{ color: '#6b7280', fontSize: '12px' }}>{d.os}</span>
                      <span style={{ color: '#6b7280', fontSize: '12px' }}>{d.net}</span>
                      <span style={{
                        fontSize: '9px',
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: '4px',
                        width: 'fit-content',
                        background: d.tier === 'FLAGSHIP' ? '#1e3a5f' : '#292524',
                        color: d.tier === 'FLAGSHIP' ? '#93c5fd' : '#a8a29e'
                      }}>{d.tier}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Block 2: Testing Signals We Capture */}
            <div className="hp-tech-block" style={{ marginBottom: '36px', textAlign: 'center' }}>
              <div style={{ color: '#f97316', fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Testing Signals We Capture</div>
              <h3 style={{ color: '#fff', fontSize: '24px', fontWeight: 700, maxWidth: '560px', margin: '12px auto 0', lineHeight: 1.2 }}>Each test session captures critical data points to help engineers debug faster.</h3>
              <p style={{ color: '#9ca3af', fontSize: '15px', lineHeight: 1.7, maxWidth: '480px', margin: '16px auto 0' }}>
                Our platform captures full screen recordings of the user journey alongside comprehensive device metadata and network conditions. Every bug report includes direct user feedback and technical logs to pinpoint exactly what broke and why.
              </p>

              <div style={{ position: 'relative', maxWidth: '760px', margin: '32px auto 0' }}>
                <div style={{
                  position: 'absolute',
                  inset: '-60px',
                  background: 'radial-gradient(ellipse, rgba(249,115,22,0.12) 0%, transparent 65%)',
                  filter: 'blur(60px)',
                  zIndex: -1
                }} />
                <div style={{
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: '1px solid rgba(255,255,255,0.08)',
                  boxShadow: '0 32px 64px rgba(0,0,0,0.6)'
                }}>
                  <img src={recordingImg} alt="AI Report Detail" style={{ width: '100%', height: '340px', objectFit: 'cover', objectPosition: 'top left', display: 'block' }} />
                </div>
              </div>
            </div>

            {/* Block 3: Data Safety */}
            <div className="hp-tech-block" style={{ textAlign: 'center' }}>
              <div style={{ color: '#f97316', fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Data Safety</div>
              <h3 style={{ color: '#fff', fontSize: '24px', fontWeight: 700, maxWidth: '560px', margin: '12px auto 0', lineHeight: 1.2 }}>We prioritize your application's security. We do not access your backend, database, or source code.</h3>
              <p style={{ color: '#9ca3af', fontSize: '15px', lineHeight: 1.7, maxWidth: '480px', margin: '16px auto 0' }}>
                Testers use non-personal dummy data in isolated sessions to ensure your production environment remains secure. All test recordings and reports are encrypted in transit and at rest.
              </p>

              <div style={{
                marginTop: '32px',
                maxWidth: '760px',
                margin: '32px auto 0',
                display: 'flex',
                gap: '16px',
                flexWrap: 'wrap'
              }}>
                {[
                  {
                    title: 'End-to-end encrypted',
                    desc: 'All recordings and reports are encrypted in transit and at rest.',
                    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                  },
                  {
                    title: 'No device data stored',
                    desc: 'Test sessions run in isolated environments. No local data remains on the tester device.',
                    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                  },
                  {
                    title: 'No source code access',
                    desc: 'We only test your APK. We never touch your backend, database, or source code.',
                    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  }
                ].map((card, i) => (
                  <div key={i} style={{
                    flex: 1,
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '12px',
                    padding: '20px 24px',
                    textAlign: 'left'
                  }}>
                    <div style={{ color: '#9ca3af', marginBottom: '16px' }}>{card.icon}</div>
                    <div style={{ color: '#fff', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>{card.title}</div>
                    <div style={{ color: '#6b7280', fontSize: '12px', lineHeight: 1.6 }}>{card.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Sample Bug Report ── */}
      <section id="sample-reports" className="hp-section--sample-reports">
        <div className="hp-sample-reports-container">
          <div className="hp-sample-intro">
            <AnimatedShinyText className="hp-section-badge">The Teardown</AnimatedShinyText>
            <h2 className="hp-sample-h2">This is what your team gets.</h2>
            <p className="hp-sample-p">
              A real report from a real session. One tester, one phone, five minutes. This is the level of detail every test produces.
            </p>
          </div>
          <SampleReportCard />
        </div>
      </section>

      {/* ── Builders / Meet the Team ── */}
      <BuildersSection />

      {/* ── Features ── */}
      <section className="hp-section--alt" id="features">
        <div ref={featRef} className={`reveal ${featVis ? 'reveal--on' : ''}`}>
          <TextAnimate as="h2" animation="blurInUp" by="word" className="hp-section-title">
            What you get
          </TextAnimate>
          <TextAnimate animation="blurInUp" by="word" className="hp-section-sub">
            Everything you need to ship with confidence.
          </TextAnimate>
          <ul className="hp-features" style={{ gridTemplateColumns: 'repeat(2, 1fr)', maxWidth: '900px', margin: '0 auto' }}>
            {[
              [<Bug key="f1" size={24} />, 'Structured bug reports', 'Clear, concise reports with technical metadata and severity tagging.'],
              [<Video key="f2" size={24} />, 'Screen recordings', 'Full video evidence for every issue identified during the session.'],
              [<Smartphone key="f3" size={24} />, 'Device + OS breakdown', 'Exact telemetry of the hardware and software used during the test.'],
              [<List key="f4" size={24} />, 'Reproduction steps', 'Step-by-step logs of how the tester triggered the bug.'],
            ].map(([icon, title, desc], i) => (
              <li key={i} style={{ animationDelay: `${i * 80}ms`, padding: '30px' }}>
                <span className="hp-feat-icon">{icon}</span>
                <strong>{title}</strong>
                <span>{desc}</span>
              </li>
            ))}
          </ul>
          <div style={{ textAlign: 'center', marginTop: '40px', color: 'var(--hp-fg2)', fontSize: '18px', fontWeight: 500 }}>
            Everything you need to fix before launch.
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section className="hp-section" id="pricing">
        <div className={`reveal ${featVis ? 'reveal--on' : ''}`}>
          <TextAnimate as="h2" animation="blurInUp" by="word" className="hp-section-title">
            Transparent Pricing
          </TextAnimate>
          <TextAnimate animation="blurInUp" by="word" className="hp-section-sub">
            We pay testers directly. You only pay for what ships.
          </TextAnimate>
          <div className="hp-pricing-grid">

            {/* STARTER */}
            <div className="hp-pricing-card" style={{ position: 'relative' }}>
              <ShineBorder shineColor="#FF6B2B" duration={10} className="hp-pricing-shine" />
              <h3>STARTER</h3>
              <div className="hp-pricing-label">Best for: first launch or beta</div>
              <div className="hp-pricing-price" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{ fontSize: '16px', color: 'rgba(255,255,255,0.4)', textDecoration: 'line-through', fontWeight: '600', marginBottom: '-6px' }}>₹1,999</div>
                <div>₹899<span className="hp-pricing-period">/one-time</span></div>
                <div style={{ fontSize: '11px', color: '#0ff08a', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700', marginTop: '8px', background: 'rgba(15,240,138,0.1)', padding: '4px 8px', borderRadius: '4px', border: '1px solid rgba(15,240,138,0.2)' }}>Limited time steal</div>
              </div>

              <div className="hp-pricing-divider" />

              <ul className="hp-pricing-features">
                <li>20 Real Testers</li>
                <li>1 Testing Iteration</li>
                <li>Basic Demographics</li>
                <li>Standard Support</li>
              </ul>
              <InteractiveButton
                variant="outline"
                className="hp-pricing-btn"
                onClick={() => {
                  if (company) onNavigate('dashboard');
                  else onNavigate('login', { plan: 'Starter', price: '₹899/one-time' });
                }}
              >
                Get Started
              </InteractiveButton>
            </div>

            {/* GROWTH */}
            <div className="hp-pricing-card hp-pricing-card--featured" style={{ position: 'relative' }}>
              <ShineBorder shineColor={["#FF6B2B", "#FFD700"]} duration={8} className="hp-pricing-shine" />
              <div className="hp-pricing-recommended">RECOMMENDED</div>
              <h3>GROWTH</h3>
              <div className="hp-pricing-label">Best for: weekly releases</div>

              <div className="hp-pricing-price">
                ₹4,999<span className="hp-pricing-period">/mo</span>
              </div>

              <div className="hp-pricing-divider" />

              <ul className="hp-pricing-features">
                <li>20 Real Testers</li>
                <li>2 Testing Iterations</li>
                <li>Advanced Targeting</li>
                <li>Priority Support</li>
              </ul>
              <InteractiveButton
                variant="primary"
                filled={true}
                className="hp-pricing-btn"
                onClick={() => {
                  if (company) onNavigate('dashboard');
                  else onNavigate('login', { plan: 'Growth', price: '₹4,999/mo' });
                }}
              >
                Get Started
              </InteractiveButton>
            </div>

            {/* SCALE */}
            <div className="hp-pricing-card" style={{ position: 'relative' }}>
              <ShineBorder shineColor="#FF6B2B" duration={10} className="hp-pricing-shine" />
              <h3>SCALE</h3>
              <div className="hp-pricing-label">Best for: fast-shipping teams</div>

              <div className="hp-pricing-price">
                ₹9,999<span className="hp-pricing-period">/mo</span>
              </div>

              <div className="hp-pricing-divider" />

              <ul className="hp-pricing-features">
                <li>40 Real Testers</li>
                <li>2 Testing Iterations</li>
                <li>Premium Targeting</li>
                <li>24/7 Dedicated Support</li>
              </ul>
              <InteractiveButton
                variant="outline"
                className="hp-pricing-btn"
                onClick={() => {
                  if (company) onNavigate('dashboard');
                  else onNavigate('login', { plan: 'Scale', price: '₹9,999/mo' });
                }}
              >
                Get Started
              </InteractiveButton>
            </div>



          </div>
        </div>
      </section>

      {/* ── FAQ Section ── */}
      <section className="hp-section hp-section--faq" id="faq">
        <div className="hp-faq-container">
          <AnimatedShinyText className="hp-section-badge">Got questions?</AnimatedShinyText>
          <TextAnimate as="h2" animation="blurInUp" by="word" className="hp-section-title">
            Frequently Asked Questions
          </TextAnimate>
          <p className="hp-section-sub">Everything you need to know about testing with BharatQA.</p>
          
          <div className="hp-faq-list">
            {FAQS.map((faq, i) => (
              <FaqItem key={i} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="hp-section--cta">
        <div ref={ctaRef} className={`hp-cta-inner reveal ${ctaVis ? 'reveal--on' : ''}`}>
          <div>
            <TextAnimate as="h2" animation="blurInUp" by="word" className="hp-cta-inner-title">
              Ship with confidence. Know it works everywhere.
            </TextAnimate>
            <p>
              Join developers who catch real-device bugs before their users do.
            </p>
          </div>
          <div className="hp-cta-right">
            {company ? (
              <InteractiveButton
                variant="light"
                onClick={() => onNavigate('dashboard')}
              >
                Go to Dashboard →
              </InteractiveButton>
            ) : (
              <div className="hp-login-wrap hp-login-wrap--center">
                <GoogleLogin
                  onSuccess={onLogin}
                  onError={() => alert('Login failed')}
                  theme="outline"
                  size="large"
                  text="signin_with"
                  shape="rectangular"
                  width="280"
                />
                <p className="hp-login-note">
                  No credit card required
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="hp-footer">
        <div className="hp-footer-inner">
          <div className="hp-footer-col">
            <div className="hp-logo hp-logo--footer">
              <img src={logo} alt="BharatQA" className="hp-logo-img hp-logo-img--footer" />
            </div>
            <p className="hp-footer-tagline">
              Crowdsourced QA testing for Android apps.<br />Built for Indian developers.
            </p>
          </div>
          <div className="hp-footer-col">
            <h4>Office</h4>
            <address>
              Shadat Colony, Gudihal Road,<br />
              Vishal Nagar, Hubballi
            </address>
          </div>
          <div className="hp-footer-col">
            <h4>Contact</h4>
            <p><a href="tel:+916361434273">+91 6361434273</a></p>
            <p><a href="tel:+918123548635">+91 8123548635</a></p>
            <p><a href="mailto:fundup3@gmail.com">fundup3@gmail.com</a></p>
            <p><a href="mailto:zaheer@fundup7.in">zaheer@fundup7.in</a></p>
          </div>
          <div className="hp-footer-col">
            <h4>Social</h4>
            <div className="hp-footer-socials">
              <a href="https://github.com/fundup7" target="_blank" rel="noopener noreferrer"><Github size={18} /> <span>GitHub</span></a>
              <a href="https://www.instagram.com/bharatqa/" target="_blank" rel="noopener noreferrer"><Instagram size={18} /> <span>Instagram</span></a>
              <a href="https://www.linkedin.com/company/bharatqa/" target="_blank" rel="noopener noreferrer"><Linkedin size={18} /> <span>LinkedIn</span></a>
            </div>
          </div>
        </div>
        <div className="hp-footer-bottom">
          <span>© {new Date().getFullYear()} BharatQA. All rights reserved.</span>
          <div className="hp-footer-links">
            <button onClick={() => onNavigate('changelog')}>Changelog / Status</button>
            <button onClick={() => onNavigate('terms')}>Terms of Service</button>
            <button onClick={() => onNavigate('privacy')}>Privacy Policy</button>
          </div>
          <span>Made in India 🇮🇳</span>
        </div>
      </footer>
    </div>
  );
}